import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { getCompanyConfigCached } from '../utils/invoiceUtils';
import { apiUrl } from '../config/api';
import {
  X,
  Home,
  UserPlus,
  Shield,
  Settings,
  Users,
  UserCheck,
  User,
  IdCard,
  Percent,
  CreditCard,
  List,
  BarChart3,
  FileText,
  LifeBuoy,
  ChevronLeft,
  ChevronRight,
  Receipt,
  Mail,
  ShieldAlert,
  FileWarning,
  KeyRound,
  Banknote,
} from 'lucide-react';


const Sidebar = ({ onCloseMobile }) => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  // Publish the sidebar's current width as a CSS variable so overlays
  // (FullScreenPanel popups) can start after the menu instead of covering it.
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--app-sidebar-w',
      collapsed ? '4rem' : '16rem'
    );
    return () => {
      document.documentElement.style.removeProperty('--app-sidebar-w');
    };
  }, [collapsed]);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);

  const perms = Array.isArray(user?.permissions) ? user.permissions : [];
  const isClientUser = perms.includes('SignDocuments') || perms.includes('ViewProfile');

  // Every menu item responds, even for the page already open: going to the
  // same path again with a new state.refresh makes DashboardLayout mount the
  // page afresh, so it reloads its data in place (no browser reload). Any
  // filters in the address (?status=...) are cleared by the same click.
  const handleLinkClick = (e, to) => {
    if (onCloseMobile) onCloseMobile();
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    navigate(to, { state: { refresh: Date.now() } });
  };

  const links = [
    { to: '/dashboard', label: 'Dashboard', icon: <Home size={18} /> },
    { to: '/manage-clients', label: 'Clients', permission: 'ManageClients', icon: <User size={18} /> },
    ...(!isClientUser ? [{ to: '/manage-loans', label: 'Loans', permission: ['ViewLoans', 'ManageLoans'], icon: <CreditCard size={18} /> }] : []),
    // Every repayment across the loans (staff; a client sees theirs on the dashboard).
    ...(!isClientUser ? [{ to: '/repayments', label: 'Repayments', permission: ['*', 'ViewLoans', 'ManageLoans', 'EnterRepayment'], icon: <Banknote size={18} /> }] : []),
    { to: '/client/profile', label: 'My Profile', permission: 'ViewProfile', icon: <IdCard size={18} /> },
    { to: '/client-form', label: 'Client Onboarding', permission: 'ClientOnboarding', icon: <UserPlus size={18} /> },
    { to: '/company-config', label: 'Company Config', permission: ['CompanyConfig', 'ManageConfig'], icon: <Settings size={18} /> },
    { to: '/manage-users', label: 'Users', permission: 'ManageUsers', icon: <Users size={18} /> },
    { to: '/manage-roles', label: 'Roles', permission: 'ManageConfig', icon: <UserCheck size={18} /> },
    { to: '/manage-interest-rates', label: 'Interest Rates', permission: 'ManageConfig', icon: <Percent size={18} /> },
    { to: '/manage-loan-reasons', label: 'Loan Reasons', permission: ['LoanReason.View', 'LoanReason.Create', 'LoanReason.Edit', 'LoanReason.Toggle', 'ManageConfig'], icon: <List size={18} /> },
    { to: '/manage-loan-expenses', label: 'Loan Expenses', permission: 'ManageConfig', icon: <Receipt size={18} /> },
    { to: '/messages', label: 'Messages', permission: ['ManageClients', 'ManageLoans', 'ManageConfig', 'ManageUsers'], icon: <Mail size={18} /> },
    { to: '/reports', label: 'Reports', permission: 'GenerateReports', icon: <BarChart3 size={18} /> },
    { to: '/namfisa-reports', label: 'NAMFISA Reports', permission: ['GenerateReports', 'ManageConfig'], icon: <FileText size={18} /> },
    { to: '/compliance-cases', label: 'Compliance Cases', permission: 'ManageCompliance', icon: <ShieldAlert size={18} /> },
    { to: '/document-flags', label: 'Flagged Documents', permission: ['ManageClients', 'ManageLoans', 'ViewLoans'], icon: <FileWarning size={18} /> },
    { to: '/audits', label: 'Audit Trail', permission: 'AuditTrail', icon: <Shield size={18} /> },
    { to: '/my-permissions', label: 'My Permissions', icon: <KeyRound size={18} /> },
    ...(!isClientUser ? [{ to: '/support', label: 'Support', icon: <LifeBuoy size={18} /> }] : []),
  ];

  const [companyName, setCompanyName] = useState('');
  useEffect(() => {
    (async () => {
      try {
        const cfg = await getCompanyConfigCached();
        setCompanyName(cfg?.company_name || '');
      } catch {}
    })();
  }, []);

  useEffect(() => {
    let mounted = true;
    if (isClientUser) {
      setSupportUnreadCount(0);
      return () => { mounted = false; };
    }
    const fetchUnread = async () => {
      if (!user) { if (mounted) setSupportUnreadCount(0); return; }
      try {
        const token = localStorage.getItem('token');
        if (!token) { if (mounted) setSupportUnreadCount(0); return; }
        const res = await axios.get(apiUrl('/api/tickets/unread-count'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const n = Number(res?.data?.count || 0);
        if (mounted) setSupportUnreadCount(Number.isFinite(n) ? n : 0);
      } catch {}
    };
    const onUnreadChanged = () => fetchUnread();
    fetchUnread();
    window.addEventListener('tickets:unread-changed', onUnreadChanged);
    const interval = setInterval(fetchUnread, 60000);
    return () => {
      mounted = false;
      window.removeEventListener('tickets:unread-changed', onUnreadChanged);
      clearInterval(interval);
    };
  }, [user?.id, isClientUser]);

  const visibleLinks = links.filter(link => {
    if (!user) return false;
    if (!link.permission) return true;
    if (Array.isArray(link.permission)) return link.permission.some(p => perms.includes(p));
    return perms.includes(link.permission);
  });

  // The collapse (icons only) is a desktop thing: below lg the sidebar is a
  // drawer over the page and always shows its labels, whatever was chosen on
  // a wide window before. Done with lg: classes so a resize needs no listener.
  return (
    <div
      className={`h-full bg-white text-slate-800 border-r border-gray-200 dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700/60 flex flex-col transition-all duration-300 w-64 ${
        collapsed ? 'lg:w-16' : 'lg:w-64'
      } overflow-hidden`}
    >
      {/* â”€â”€ Brand header â”€â”€ */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 dark:border-slate-700/60">
        <div className={`flex items-center gap-2 min-w-0 ${collapsed ? 'lg:hidden' : ''}`}>
          <div className="w-7 h-7 rounded-md bg-blue-500 flex items-center justify-center flex-shrink-0">
            <CreditCard size={14} className="text-white" />
          </div>
          <span className="font-semibold text-gray-900 dark:text-white text-sm truncate">
            {companyName || ''}
          </span>
        </div>
        <div className={`w-7 h-7 rounded-md bg-blue-500 items-center justify-center mx-auto hidden ${collapsed ? 'lg:flex' : ''}`}>
          <CreditCard size={14} className="text-white" />
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Mobile close */}
          {onCloseMobile && (
            <button
              onClick={onCloseMobile}
              className="lg:hidden p-1.5 text-slate-500 hover:text-slate-900 hover:bg-gray-100 dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-700 rounded-md transition-colors"
              aria-label="Close sidebar"
            >
              <X size={18} />
            </button>
          )}
          {/* Desktop collapse toggle */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:flex p-1.5 text-slate-500 hover:text-slate-900 hover:bg-gray-100 dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-700 rounded-md transition-colors"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>
      </div>

      {/* â”€â”€ Nav links â”€â”€ */}
      <nav className="flex-1 py-2 px-2 overflow-y-auto [&>div]:border-b [&>div]:border-gray-200 dark:[&>div]:border-slate-700/80">
        {visibleLinks.map(link => {
          const isActive = location.pathname === link.to;
          const isSupportLink = link.to === '/support';
          const showBadge = isSupportLink && supportUnreadCount > 0;
          const badgeText = supportUnreadCount > 99 ? '99+' : String(supportUnreadCount);

          // Each menu sits in its own row, with a line under it. The active
          // item's text is white in every state (hover, dark): the global link
          // colours (index.css: a, a:hover, .dark a) otherwise win and turn
          // it blue on the blue background.
          return (
            <div key={link.to} className="py-1">
            <Link
              to={link.to}
              onClick={(e) => handleLinkClick(e, link.to)}
              title={collapsed ? link.label : undefined}
              className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${
                isActive
                  ? 'bg-blue-600 text-white shadow-sm hover:bg-blue-700 hover:text-white dark:bg-blue-600 dark:text-white dark:hover:bg-blue-500 dark:hover:text-white'
                  : 'text-slate-700 hover:bg-blue-50 hover:text-blue-700 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-white'
              }`}
            >
              <span className={`relative flex-shrink-0 ${isActive ? 'text-white' : 'text-blue-600 dark:text-blue-300'}`}>
                {link.icon}
                {showBadge && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center leading-none">
                    {badgeText}
                  </span>
                )}
              </span>
              <span className={`truncate ${collapsed ? 'lg:hidden' : ''}`}>{link.label}</span>
            </Link>
            </div>
          );
        })}
      </nav>

      {/* User profile, theme toggle and logout were moved to the top-bar
          (DashboardLayout â†’ Topbar) so they sit beside the page title. */}
    </div>
  );
};

export default Sidebar;
